import React, { useEffect, useRef } from "react";
import { Camera, CloudSun, RadioTower, SatelliteDish, TriangleAlert, Video } from "lucide-react";
import HlsPlayer from "../components/HlsPlayer";
import { StormDeskRadar } from "../components/StormDeskRadar";
import { SkycamPlayer } from "../components/SkycamPlayer";
import { useChaserStream } from "./ChaserStreamContext";

const sceneMeta = {
  radar_full: { label: "Storm Desk Radar", icon: SatelliteDish },
  breaking_news: { label: "Severe Weather", icon: TriangleAlert },
  analysis: { label: "Weather Analysis", icon: SatelliteDish },
  chaser_cam: { label: "Storm Chaser Camera", icon: Camera },
  forecast: { label: "Forecast", icon: CloudSun },
  live_source: { label: "Live Source", icon: RadioTower },
  skycam: { label: "HazCam Skycam", icon: Video },
};

export const SceneMonitor = ({ mode, sceneId, lowerThird, source, skycam }) => {
  const meta = sceneMeta[sceneId] || sceneMeta.radar_full;
  const Icon = meta.icon;
  const isRadar = ["radar_full", "breaking_news", "analysis"].includes(sceneId);
  const program = mode === "program";
  const { stream: chaserStream } = useChaserStream();
  const isChaser = sceneId === "chaser_cam" && Boolean(chaserStream);
  const videoRef = useRef(null);

  useEffect(() => { if (videoRef.current) videoRef.current.srcObject = isChaser ? chaserStream : null; }, [isChaser, chaserStream]);

  return (
    <section
      data-testid={`${mode}-monitor`}
      className={`relative min-w-0 overflow-hidden border-2 bg-black ${program ? "border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.25)]" : "border-sky-500 shadow-[0_0_15px_rgba(14,165,233,0.2)]"}`}
    >
      <div className={`mono absolute left-0 top-0 z-30 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white ${program ? "bg-red-600" : "bg-sky-600"}`}>
        {program ? "PGM · PROGRAM" : "PVW · PREVIEW"}
      </div>
      <div className="aspect-video min-h-[200px] w-full bg-[#04060e]">
        {isRadar ? (
          <div data-testid={`${mode}-radar-frame`} className="h-full w-full">
            <StormDeskRadar height="100%" hideHeader hidePanel compact />
          </div>
        ) : isChaser ? (
          <video ref={videoRef} data-testid={`${mode}-chaser-video`} autoPlay playsInline muted className="h-full w-full object-contain" />
        ) : sceneId === "skycam" && skycam?.video_url ? (
          <div data-testid={`${mode}-skycam-frame`} className="h-full w-full">
            <SkycamPlayer cam={skycam} compact testId={`${mode}-skycam-player`} />
          </div>
        ) : source?.src ? (
          <HlsPlayer src={source.src} poster={source.poster} testIdPrefix={`${mode}-source-player`} />
        ) : (
          <div className="grid h-full place-items-center text-center">
            <div>
              <Icon className="mx-auto h-7 w-7 text-slate-600" />
              <div className="display mt-2 text-lg font-bold uppercase text-slate-300">{meta.label}</div>
              <div className="mono mt-1 text-[9px] uppercase tracking-widest text-slate-600">Source not connected</div>
            </div>
          </div>
        )}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex items-end justify-between gap-2 p-2">
        <div className="min-w-0 border border-white/10 bg-black/75 px-2 py-1 backdrop-blur-sm">
          <div className="display truncate text-xs font-bold uppercase text-white">{meta.label}</div>
          {lowerThird?.title && <div className={`mt-1 border-l-2 pl-2 text-[11px] font-semibold text-white ${program ? "border-red-500" : "border-sky-500"}`}>{lowerThird.title}</div>}
          {lowerThird?.subtitle && <div className="truncate pl-2 text-[10px] text-slate-300">{lowerThird.subtitle}</div>}
        </div>
        <span className="mono shrink-0 border border-white/10 bg-black/75 px-2 py-1 text-[9px] uppercase tracking-widest text-slate-300">Storm Desk Live</span>
      </div>
    </section>
  );
};
